<script setup lang="ts">
import { NodeKind } from '../helpers/internal-repr';
import { nodeKindSymbols } from '../helpers/logic-symbols';
import Dropdown from 'primevue/dropdown';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { PredicateValue } from '@precon-tools-suite-by-shared/precon-graphql-types';

const props = withDefaults(
    defineProps<{
        modelValue?: NodeKind | null;
        inputId: string;
        inputName: string;
        options?: NodeKind[];
        readonly?: boolean;
        symbolLabel?: boolean;
        testValue?: PredicateValue;
    }>(),
    {
        modelValue: null,
        options: () => Object.values(NodeKind),
        testValue: PredicateValue.Indeterminate,
    }
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: NodeKind): void;
}>();

const { t } = useI18n();

const testValueClasses: Record<PredicateValue, string> = {
    [PredicateValue.Yes]: 'bg-green-100 border-green-200',
    [PredicateValue.No]: 'bg-pink-100 border-pink-200',
    [PredicateValue.Undecidable]: 'bg-yellow-100',
    [PredicateValue.Indeterminate]: '',
};

const value = computed<NodeKind | null>({
    get: () => props.modelValue,
    set: (newValue: NodeKind | null) => {
        if (newValue) emit('update:modelValue', newValue);
    },
});

const testValueClass = computed(() => {
    return testValueClasses[props.testValue];
});

const valueLabel = computed(() => {
    if (!value.value) return '';

    if (props.symbolLabel) {
        return nodeKindSymbols[value.value];
    } else {
        return t(`label.${value.value}`);
    }
});
</script>

<template>
    <div
        v-if="readonly"
        class="expr-node-kind-selector expr-node-kind-selector-readonly"
        :class="testValueClass"
    >
        <span class="expr-node-kind-label">{{ valueLabel }}</span>
    </div>
    <Dropdown
        v-else
        v-model="value"
        v-bind="$attrs"
        class="expr-node-kind-selector"
        :class="testValueClass"
        :options="options"
        :input-id="inputId"
        :name="inputName"
    >
        <template #value>
            {{ valueLabel }}
        </template>
        <template #option="{ option }">
            <span class="expr-node-kind-symbol">{{
                nodeKindSymbols[option]
            }}</span>
            <span>{{ t(`title.${option}`) }}</span>
        </template>
    </Dropdown>
</template>

<style lang="scss">
.expr-node-kind-selector {
    .p-dropdown-trigger {
        display: none;
    }

    &.expr-node-kind-selector-readonly {
        border: 1px solid var(--surface-border);
        border-radius: var(--border-radius);
        display: inline-flex;
        position: relative;
        align-items: center !important;
        padding: 0.75rem;
        background-color: var(--surface-a);
    }
}

.expr-node-kind-symbol {
    width: 1.8em;
    margin-left: -1em;
    text-align: center;
    font-weight: 1000;
    display: inline-block;
    color: var(--primary-color);
}
</style>

<i18n>
    {
        "nl": {
            "label": {
                "value": "Altijd",
                "term": "Pr.",
                "conj": "En",
                "disj": "Of",
                "neg": "Niet",
                "implies": "Als",
                "equiv": "⇔"
            },
            "title": {
                "value": "Vaste waarde",
                "term": "Predicaat",
                "conj": "En",
                "disj": "Of",
                "neg": "Niet",
                "implies": "Als … dan …",
                "equiv": "… dan en slechts dan …"
            },
        },
        "en": {
            "label": {
                "value": "Always",
                "term": "Pr.",
                "conj": "And",
                "disj": "Or",
                "neg": "Not",
                "implies": "If",
                "equiv": "Iff"
            },
            "title": {
                "value": "Constant value",
                "term": "Predicate",
                "conj": "And",
                "disj": "Or",
                "neg": "Not",
                "implies": "If … then …",
                "equiv": "… if and only if …"
            }
        },
        "fr": {
            "label": {
                "value": "Const.",
                "term": "Pr.",
                "conj": "Et",
                "disj": "Ou",
                "neg": "Pas",
                "implies": "Si",
                "equiv": "Ssi"
            },
            "title": {
                "value": "Constante",
                "term": "Prédicat",
                "conj": "Et",
                "disj": "Ou",
                "implies": "Si",
                "equiv": "Si et seulement si"
            }
        }
    }
</i18n>
